<template>
  <v-expansion-panels class="my-3">
    <v-expansion-panel v-if="!touristData.need_pre_checkin">
      <v-expansion-panel-header
        class="pa-3"
        expand-icon="mdi-menu-down"
        @click="track('guide_opened', null, $event)"
      >
        <div><v-icon class="px-3"> $video </v-icon>{{ $t('Guidebook') }}</div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-expansion-panels flat>
          <v-expansion-panel v-for="(category, key) in categories" :key="key">
            <v-expansion-panel-header
              class="px-0 font-weight-medium text-body-2"
              expand-icon="mdi-menu-down"
              @click="track('guide_block_opened', category.topic, $event)"
              >{{ category.title }}</v-expansion-panel-header
            >
            <v-expansion-panel-content class="sections-panel pe-4">
              <v-expansion-panels flat>
                <v-expansion-panel
                  v-for="(section, sectionKey) in category.sections"
                  :key="sectionKey"
                >
                  <v-expansion-panel-header
                    class="px-0 font-weight-medium text-body-2"
                    expand-icon="mdi-menu-down"
                    @click="
                      track(
                        'guide_block_opened',
                        `${category.topic}.${section.subTopic}`,
                        $event
                      )
                    "
                  >
                    <span>
                      <span
                        v-for="(header, i) in section.headers"
                        :key="header"
                        class="text-caption text-capitalize mt-2 pb-2 font-weight-medium"
                      >
                        <span> {{ header }}</span>
                        <v-icon v-if="section.headers.length - 1 !== i" small
                          >mdi-chevron-right</v-icon
                        >
                      </span></span
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content class="inner-panel">
                    <v-row>
                      <v-col cols="12" class="overflow-hidden pt-0">
                        <div
                          style="white-space: pre-line"
                          class="text-caption mt-1"
                        >
                          <div v-html="marked(section.comments)"></div>
                        </div>
                        <gallery
                          button-height="350px"
                          :thumbnails="true"
                          :cols="12"
                          :images="section.images"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                  <v-divider />
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <div v-else class="text-center error--text text-body-3 text-sm-body-1">
      Please complete the Pre Check-in process
    </div>
    <v-expansion-panel
      v-for="device in remoteUnlockDevices"
      :key="device.device_id"
      :disabled="disabled"
    >
      <v-expansion-panel-header class="pa-3" expand-icon="mdi-menu-down">
        <div>
          <v-icon class="px-3">{{
            isDeviceLocked(device) ? 'mdi-lock' : 'mdi-lock-open-variant'
          }}</v-icon>
          {{ device.entry_point || 'Remote Door Control' }}
        </div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div class="px-2 pb-2">
          <!-- Action Button -->
          <v-btn
            :color="isDeviceLocked(device) ? 'success' : 'error'"
            depressed
            block
            :loading="isDeviceBusy(device)"
            :disabled="isDeviceBusy(device) || !device.has_lock_capability"
            @click="toggleDoor(device)"
          >
            <v-icon left small>{{
              isDeviceLocked(device) ? 'mdi-lock-open-variant' : 'mdi-lock'
            }}</v-icon>
            {{ isDeviceLocked(device) ? $t('Unlock') : $t('Lock') }}
          </v-btn>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel v-if="hasDoorCodes" :disabled="disabled">
      <v-expansion-panel-header
        class="pa-3"
        expand-icon="mdi-menu-down"
        @click="track('access_code_viewed', null, $event)"
      >
        <div>
          <v-icon class="px-3"> $door_key </v-icon>{{ $t('DoorCodes') }}
        </div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div
          v-if="isAlert(doorCodes)"
          class="d-flex align-center pa-2 error--text"
        >
          <v-icon size="20" color="error" class="mr-2">
            fas fa-exclamation-triangle
          </v-icon>
          <span class="text-body-2">{{ doorCodes.Alert }}</span>
        </div>

        <div class="px-2">
          <v-card
            v-for="(val, key) in filteredDoorCodes"
            :key="key"
            outlined
            class="mb-3 rounded-lg"
          >
            <div class="pa-4">
              <div class="text-body-2 font-weight-medium mb-1">
                {{ formatLocation(key) }}
              </div>

              <template v-if="isUrl(val)">
                <div class="d-flex align-center bg-grey-lighten-4 rounded pa-2">
                  <a
                    :href="val.match(/https?:\/\/[^\s]+/)[0]"
                    target="_blank"
                    class="text-decoration-none"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="text-body-2">
                          {{ $t('View') }}
                          <v-icon small color="primary" class="ml-1"
                            >mdi-open-in-new</v-icon
                          >
                        </span>
                      </template>
                      <span class="text-caption">{{ val }}</span>
                    </v-tooltip>
                  </a>
                </div>
              </template>
              <template v-else-if="Array.isArray(val)">
                <div class="bg-grey-lighten-4 rounded pa-2">
                  <div
                    v-for="(instruction, index) in val"
                    :key="index"
                    class="d-flex mb-1 text-body-2"
                  >
                    <span class="mr-2">{{ index + 1 }}.</span>
                    <span>{{ instruction }}</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="bg-grey-lighten-4 rounded pa-2">
                  <div class="text-subtitle-2 font-weight-medium">
                    {{ val }}
                  </div>
                </div>
              </template>
            </div>
          </v-card>

          <v-card
            v-if="touristData.info.custom_instructions"
            outlined
            class="pa-4 rounded-lg"
          >
            <div class="text-subtitle-1 font-weight-medium mb-2">
              {{ $t('Custom') }}
            </div>
            <div class="bg-grey-lighten-4 rounded pa-3">
              <div class="text-body-2" style="white-space: pre-line">
                {{ touristData.info.custom_instructions }}
              </div>
            </div>
          </v-card>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel v-if="hasWifi">
      <v-expansion-panel-header class="pa-3" expand-icon="mdi-menu-down">
        <div><v-icon class="px-3"> $wifi </v-icon>{{ $t('Wifi') }}</div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div v-for="(val, key) in touristData.info.wifi" :key="key">
          <div class="d-flex align-center">
            <v-icon v-if="key === 'Alert'" size="20" color="error">
              fas fa-exclamation-triangle
            </v-icon>
            <span v-else class="text-body-1 text-capitalize"
              >{{ $t(key) }}:</span
            >
            <span
              :class="[
                'body-2',
                'ms-2',
                key === 'Alert' ? 'error--text' : 'bolder',
              ]"
            >
              {{ val }}</span
            >
          </div>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel v-if="hasContactInfo">
      <v-expansion-panel-header class="pa-3" expand-icon="mdi-menu-down">
        <div>
          <v-icon class="px-3"> $phone_call </v-icon>{{ $t('Contact Us') }}
        </div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div
          class="d-grid mt-2"
          :style="`grid-gap: ${isMobile ? '1rem' : '3rem'};
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(50px, 1fr))`"
        >
          <div
            v-for="(phones, key) in touristData.info.phones"
            :key="key"
            class="d-flex flex-column justify-center align-center text-center"
          >
            <div class="me-2">
              <v-icon v-if="key === 'Alert'" size="20">
                fas fa-exclamation-triangle
              </v-icon>
            </div>
            <div
              v-for="phone in phones"
              :key="phone"
              :class="[
                'body-2',
                key === 'Alert' ? 'error--text' : 'bolder',
                'text-center',
              ]"
            >
              <div v-if="key == 'Whatsapp'">
                <a target="_blank" :href="`https://wa.me/${phone}`"
                  ><v-icon color="green" large size="30">mdi-whatsapp</v-icon>
                </a>
              </div>
              <div v-else-if="key == 'SMS'">
                <a target="_blank" :href="`sms:${phone}`"
                  ><v-icon color="blue" size="30">mdi-message-text</v-icon></a
                >
              </div>
              <div v-else-if="key == 'Phone'">
                <a target="_blank" :href="`tel:${phone}`"
                  ><v-icon color="teal" size="30">mdi-phone</v-icon></a
                >
              </div>
              <div v-else>
                {{ phone }}
              </div>
            </div>
            <div
              v-if="
                !isMobile &&
                !(key === 'Whatsapp' && companyNameConf === 'Fragolaapartments')
              "
              class="text-caption secondary--text mt-1 text-no-wrap"
            >
              {{ phones[0] }}
            </div>
          </div>

          <div
            v-for="(link, key) in links"
            :key="key"
            style="text-align: center"
          >
            <a target="_blank" :href="link">
              <v-icon :color="iconMap[key].color" size="30">{{
                iconMap[key].icon
              }}</v-icon>
            </a>
            <div
              v-if="!isMobile"
              class="text-caption secondary--text mt-1 text-no-wrap"
            >
              {{ iconMap[key].name }}
            </div>
          </div>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import axios from 'axios'
import filter from 'lodash/fp/filter'
import Gallery from 'components/common/gallery.vue'
import deviceMixin from 'components/mixins/deviceMixin'
import { trackGuestActivity } from '@/utils/guestActivity'
import { groupBy, orderBy } from 'lodash'
import { marked } from 'marked'

const renderer = new marked.Renderer()
renderer.link = function (href, title, text) {
  return `<a href="${href}" title="${title}" target="_blank" rel="noopener noreferrer">${text}</a>`
}
export default {
  name: 'BookingCodes',
  components: { Gallery },
  mixins: [deviceMixin],
  props: ['touristData', 'disabled'],
  data() {
    return {
      deviceStates: {}, // { device_id: { busy: false, error: null, locked: null } }
      iconMap: {
        facebook: { icon: 'mdi-facebook', color: 'blue', name: 'Facebook' },
        google: { icon: 'mdi-google', color: 'red', name: 'Google' },
        linkedin: { icon: 'mdi-linkedin', color: 'blue', name: 'LinkedIn' },
        website: { icon: 'mdi-web', color: 'green', name: 'Website' },
        booking_site: {
          icon: 'mdi-web-box',
          color: 'blue',
          name: 'Booking Site',
        },
        instagram: {
          icon: 'mdi-instagram',
          color: 'purple',
          name: 'Instagram',
        },
      },
    }
  },
  computed: {
    remoteUnlockDevices() {
      const devices = this.touristData?.remote_unlock || []
      return devices.filter(device => device.available)
    },
    doorCodes() {
      return this.touristData.info['door_codes']
    },
    filteredDoorCodes() {
      if (!this.doorCodes) return {}
      const codes = { ...this.doorCodes }
      delete codes.Alert
      return codes
    },
    links() {
      return this.touristData.info.links || []
    },
    hasDoorCodes() {
      return filter(Boolean, this.doorCodes).length !== 0
    },
    hasContactInfo() {
      return (
        (this.touristData.info.phones &&
          Object.keys(this.touristData.info.phones).length > 0) ||
        this.links.length > 0
      )
    },
    hasWifi() {
      return (
        this.touristData.info.wifi &&
        Object.keys(this.touristData.info.wifi).length > 0
      )
    },
    categories() {
      const filteredGuestsInfo = this.touristData.guests_info.filter(
        info => info.listing_info_def_id
      )
      const { locale } = this.$i18n
      const categories = groupBy(filteredGuestsInfo, 'listing_info_def.topic')
      const grouped = Object.entries(categories).map(([key, items]) => ({
        title: items[0]?.listing_info_def?.localization?.[locale]?.topic ?? key,
        // The untranslated topic, kept for reporting: the title is localised and the
        // v-for index moves whenever a host adds a topic, so neither identifies a
        // block across two guests or two days.
        topic: key,
        sections: this.sections(items),
      }))
      return orderBy(grouped, 'title')
    },
  },
  methods: {
    // One line per place worth knowing about, and nothing to handle here: the tracker
    // buffers, folds repeats and swallows its own failures.
    //
    // A panel header fires this on the way open AND on the way shut, so the click
    // that closes an already-open block is dropped — otherwise "blocks opened" would
    // count each one twice for anybody tidying up after themselves. The class is read
    // before Vuetify toggles it, so it still describes the state being left.
    track(event, subject, ev) {
      const el = ev && ev.currentTarget
      if (el && el.classList.contains('v-expansion-panel-header--active'))
        return

      trackGuestActivity(event, subject)
    },
    marked(val) {
      return val ? marked(val, { renderer: renderer }) : val
    },
    sections(categoryInfo) {
      const orderedCategories = orderBy(
        categoryInfo,
        'listing_info_def.sub_topic'
      )
      const { locale } = this.$i18n
      return (orderedCategories || []).map(info => {
        const { listing_info_def, comments, images, movie_url, localization } =
          info
        const {
          sub_topic,
          hint,
          localization: defLocalization,
        } = listing_info_def
        const translatedSubTopic =
          defLocalization?.[locale]?.sub_topic || sub_topic
        const translatedHint = defLocalization?.[locale]?.hint || hint
        return {
          headers: [translatedSubTopic, translatedHint].filter(Boolean),
          // As above: the reporting key, before translation.
          subTopic: sub_topic,
          comments: localization?.[locale] || comments,
          images: movie_url ? [...(images || []), movie_url] : images,
        }
      })
    },
    isAlert(codes) {
      return codes && 'Alert' in codes
    },
    formatLocation(key) {
      return key.replace(/_/g, ' ')
    },
    isUrl(value) {
      return typeof value === 'string' && value.toLowerCase().includes('http')
    },
    initDeviceState(deviceId) {
      if (!this.deviceStates[deviceId]) {
        this.$set(this.deviceStates, deviceId, {
          busy: false,
          error: null,
          locked: null,
        })
      }
    },
    isDeviceBusy(device) {
      return this.deviceStates[device.device_id]?.busy || false
    },
    isDeviceLocked(device) {
      const state = this.deviceStates[device.device_id]
      return state?.locked !== null
        ? state.locked
        : device.locked !== null
        ? device.locked
        : true
    },
    getDeviceError(device) {
      return this.deviceStates[device.device_id]?.error || null
    },
    async toggleDoor(device) {
      this.initDeviceState(device.device_id)
      this.$set(this.deviceStates[device.device_id], 'busy', true)
      this.$set(this.deviceStates[device.device_id], 'error', null)

      try {
        const confirmationCode = encodeURIComponent(this.$route.params.id)
        const reservationGuestyId = this.$route.query?.id
        const payload = {
          device_id: device.device_id,
        }
        if (reservationGuestyId)
          payload.reservation_guesty_id = reservationGuestyId

        // Trigger the action
        const { data } = await axios.post(
          `/api/tourist/reservations/${confirmationCode}/toggle-door`,
          payload
        )

        if (!data.success) {
          this.$set(
            this.deviceStates[device.device_id],
            'error',
            'Failed to trigger door control'
          )
          return
        }

        const expectedLockedState = !data.current_locked

        // Poll for lock status change
        await this.pollLockStatus(device, confirmationCode, expectedLockedState)
      } catch (e) {
        this.$set(
          this.deviceStates[device.device_id],
          'error',
          e?.response?.data?.error ||
            e?.response?.data?.payload?.error ||
            'Failed to control door'
        )
      } finally {
        this.$set(this.deviceStates[device.device_id], 'busy', false)
      }
    },
    async pollLockStatus(device, confirmationCode, expectedLockedState) {
      const maxAttempts = 20
      const pollInterval = 1000 // 1 second
      let attempts = 0

      while (attempts < maxAttempts) {
        try {
          await new Promise(resolve => setTimeout(resolve, pollInterval))

          const { data } = await axios.get(
            `/api/tourist/reservations/${confirmationCode}/lock-status`,
            { params: { device_id: device.device_id } }
          )

          if (typeof data?.locked === 'boolean') {
            this.$set(
              this.deviceStates[device.device_id],
              'locked',
              data.locked
            )

            // Check if the lock reached the expected state
            if (data.locked === expectedLockedState) {
              return // Success!
            }
          }

          attempts++
        } catch (e) {
          console.error('Polling error:', e)
          attempts++
        }
      }

      // Timeout reached but lock state didn't change as expected
      this.$set(
        this.deviceStates[device.device_id],
        'error',
        'Door control timed out. Please check the lock manually.'
      )
    },
  },
  watch: {
    remoteUnlockDevices: {
      immediate: true,
      handler(devices) {
        if (Array.isArray(devices)) {
          devices.forEach(device => {
            this.initDeviceState(device.device_id)
            if (typeof device.locked === 'boolean') {
              this.$set(
                this.deviceStates[device.device_id],
                'locked',
                device.locked
              )
            }
          })
        }
      },
    },
  },
}
</script>

<style scoped>
.icon-center {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.theme--light.v-icon {
  color: inherit;
}
.inner-panel >>> .v-expansion-panel-content__wrap {
  padding: 0;
}
.sections-panel >>> .v-expansion-panel-content__wrap {
  padding: 0;
}
.text-break {
  word-break: break-word;
  overflow-wrap: break-word;
}
.bg-grey-lighten-4 {
  background-color: rgb(246, 246, 246) !important;
}
.rounded-lg {
  border-radius: 8px !important;
}
</style>
